/**
 * WorkspacePicker — "which workspace are you working in?", shown after sign-in to anyone with more
 * than one (tenant-scoped sign-in, 2026-10-10). Each row is the workspace name, the person's role
 * in it, and when this device last used it; the most recent comes first.
 *
 * Presentational only (Rule 33): the caller supplies the options and decides what choosing means.
 * One column at every width — a phone (375px) gets full-width rows with large touch targets inside
 * the same 16px gutter the app uses.
 */

import { Building2, ChevronRight, KeyRound } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../primitives/card.js';
import { Button } from '../primitives/button.js';
import { lastUsedLabel, orderWorkspaceOptions, type WorkspacePickerOption } from './workspace-picker.js';

export interface WorkspacePickerProps {
  readonly options: readonly WorkspacePickerOption[];
  readonly onSelect: (id: string) => void;
  /** Optional: offer "Use a workspace ID" (for a workspace that requires its link or ID). */
  readonly onUseWorkspaceId?: () => void;
  /** Disables the rows while a choice is being applied. */
  readonly busy?: boolean;
}

export function WorkspacePicker({ options, onSelect, onUseWorkspaceId, busy = false }: WorkspacePickerProps) {
  const ordered = orderWorkspaceOptions(options);
  return (
    <div className="min-h-screen flex items-start sm:items-center justify-center bg-background px-4 py-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-lg">Choose a workspace</CardTitle>
          <CardDescription>You belong to more than one. You can switch later from the header.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <ul className="space-y-2" aria-label="Your workspaces">
            {ordered.map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onSelect(o.id)}
                  className="w-full min-h-14 flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                >
                  <Building2 className="w-4 h-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">{o.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      <span className="capitalize">{o.role}</span> · {lastUsedLabel(o.lastUsedAt)}
                    </span>
                  </span>
                  <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
          {onUseWorkspaceId && (
            <Button variant="ghost" className="w-full" onClick={onUseWorkspaceId} disabled={busy}>
              <KeyRound className="w-4 h-4" aria-hidden="true" />
              Use a workspace ID
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export interface WorkspaceEntryNoticeProps {
  readonly title: string;
  readonly message: string;
  readonly primaryLabel: string;
  readonly onPrimary: () => void;
  readonly secondaryLabel?: string;
  readonly onSecondary?: () => void;
}

/**
 * The full-screen notice for a workspace that cannot be entered — a refused workspace ID or link
 * (always the same wording, whatever the reason), or a workspace that requires its link or ID.
 */
export function WorkspaceEntryNotice({ title, message, primaryLabel, onPrimary, secondaryLabel, onSecondary }: WorkspaceEntryNoticeProps) {
  return (
    <div className="min-h-screen flex items-start sm:items-center justify-center bg-background px-4 py-6">
      <Card className="w-full max-w-md" role="alert">
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
          <CardDescription>{message}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 sm:flex-row">
          <Button className="w-full sm:w-auto" onClick={onPrimary}>{primaryLabel}</Button>
          {secondaryLabel && onSecondary && (
            <Button variant="outline" className="w-full sm:w-auto" onClick={onSecondary}>{secondaryLabel}</Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
